import React from "react";
import { BsTelephone, BsMessenger, BsArrowUp, BsChatDots, BsX } from "react-icons/bs";
import { SiZalo } from "react-icons/si";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import useFooterConfig from "../hooks/useFooterConfig";

const FloatingContact = () => {
    const { footerConfig } = useFooterConfig();
    const [showScrollTop, setShowScrollTop] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const location = useLocation();

    React.useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        setIsOpen(false);
    };

    // Chỉ hiển thị ở trang chủ
    if (location.pathname !== "/") return null;

    const contactInfo = {
        phone: footerConfig?.phone || "0765.109.784",
        zalo: footerConfig?.zalo || "0765.109.784",
        messenger: "tahashoes.vn",
    };

    const buttons = [
        {
            id: "messenger",
            icon: BsMessenger,
            color: "bg-[#0084FF]",
            link: "https://m.me/tahashoes.vn",
            label: "Messenger",
            pulse: "rgba(0, 132, 255, 0.4)",
        },
        {
            id: "zalo",
            icon: SiZalo,
            color: "bg-[#0068FF]",
            link: `https://zalo.me/${contactInfo.zalo.replace(/\./g, "")}`,
            label: "Zalo",
            pulse: "rgba(0, 104, 255, 0.4)",
        },
        {
            id: "hotline",
            icon: BsTelephone,
            color: "bg-[#FF0000]",
            link: `tel:${contactInfo.phone.replace(/\./g, "")}`,
            label: "Hotline",
            pulse: "rgba(255, 0, 0, 0.4)",
        },
    ];

    const ContactItem = ({ btn, isMobile = false }) => (
        <motion.a
            key={isMobile ? `mobile-${btn.id}` : `desktop-${btn.id}`}
            href={btn.link}
            target="_blank"
            rel="noopener noreferrer"
            initial={isMobile ? { opacity: 0, scale: 0.5, y: 20 } : false}
            animate={isMobile ? { opacity: 1, scale: 1, y: 0 } : false}
            exit={isMobile ? { opacity: 0, scale: 0.5, y: 20 } : false}
            className={`relative w-12 h-12 ${btn.color} text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-transform group`}
            title={btn.label}
        >
            <div className="animate-pulse-ring" style={{ boxShadow: `0 0 0 10px ${btn.pulse}` }} />
            <div className="animate-pulse-ring" style={{ boxShadow: `0 0 0 20px ${btn.pulse}`, animationDelay: "0.25s" }} />
            <btn.icon size={20} className="relative z-10 group-hover:rotate-12 transition-transform" />
        </motion.a>
    );

    const ScrollTopBtn = ({ isMobile = false }) => (
        <motion.button
            key={isMobile ? "mobile-atop" : "desktop-atop"}
            onClick={scrollToTop}
            initial={isMobile ? { opacity: 0, scale: 0.5, y: 10 } : { opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={isMobile ? { opacity: 0, scale: 0.5, y: 10 } : { opacity: 0 }}
            className="w-12 h-12 bg-gray-800/90 backdrop-blur-md text-white rounded-full flex items-center justify-center shadow-lg border border-white/20 hover:bg-gray-700 active:scale-95 transition-all group"
            title="Quay lại đầu trang"
        >
            <BsArrowUp size={20} className="group-hover:-translate-y-1 transition-transform" />
        </motion.button>
    );

    return (
        <div className="fixed bottom-6 right-6 z-[9998] flex flex-col gap-3 items-center">
            {/* DESKTOP VERSION (Always Visible) */}
            <div className="hidden md:flex flex-col gap-3 items-center">
                {showScrollTop && <ScrollTopBtn />}
                {buttons.map((btn) => <ContactItem key={`desktop-${btn.id}`} btn={btn} />)}
            </div>

            {/* MOBILE VERSION (With Toggle) */}
            <div className="md:hidden flex flex-col gap-3 items-center">
                <AnimatePresence>
                    {isOpen && (
                        <motion.div 
                            key="mobile-menu"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            className="flex flex-col gap-3 mb-1"
                        >
                            {showScrollTop && <ScrollTopBtn isMobile />}
                            {buttons.map((btn) => <ContactItem key={`mobile-${btn.id}`} btn={btn} isMobile />)}
                        </motion.div>
                    )}

                    <motion.button
                        key="mobile-toggle"
                        onClick={() => setIsOpen(!isOpen)}
                        initial={false}
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        className={`w-12 h-12 ${isOpen ? "bg-gray-800" : "bg-red-600"} text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.2)] z-20 hover:scale-105 active:scale-95 transition-all outline-none`}
                        title={isOpen ? "Đóng liên hệ" : "Liên hệ với chúng tôi"}
                    >
                        {isOpen ? (
                            <BsX size={28} />
                        ) : (
                            <div className="relative">
                                <BsChatDots size={22} className="animate-bounce-slow" />
                                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                                </span>
                            </div>
                        )}
                    </motion.button>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default FloatingContact;
